/**
 * WindowOverEditor — the expanded body of a WindowSpec. When editing is ready
 * and the OVER body parses, it renders the STRUCTURED `WindowSpecEditor`
 * (PARTITION BY / ORDER BY / frame pickers) that replaces the old opaque
 * `<textarea>` (Task §4b-i — ANA-05/07/09/14/16/18/20/22). A small "Edit as
 * text" escape hatch still opens the raw textarea, and a corrupt / non-standard
 * OVER body that the parser can't confidently structure falls back to that raw
 * textarea automatically — a bad parse NEVER silently rewrites the user's SQL.
 *
 * "Ready" mirrors `SelectList`'s own `editReady` gate — `editContext` present
 * AND the resolver service already holds a schema snapshot — NOT mere
 * `editContext` truthiness.
 *
 * Every commit (structured or raw) goes through the SAME `modifySelectWindow`
 * op, which replaces the item's ENTIRE `OVER …` range verbatim; the structured
 * path just serializes its spec back to a whole `OVER (…)` clause first.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Pencil } from 'lucide-react';

import { Button } from '../../../../../components/ui/button';
import type { ParseOptions } from '../../../../sql-grammar/model';
import type { VqeEditContext } from '../../edit/editContext';
import { toCommitCtx } from '../../edit/editContext';
import { modifySelectWindow } from '../../edit/edits/modifySelectWindow';
import { useVqeStore } from '../../store/vqeStore';
import { vqeCopy } from '../../ux/vqeCopy';
import { VqeTooltip } from '../common/VqeTooltip';
import type { WindowItem } from './extractWindowFunction';
import { NamedWindowRefNote } from './NamedWindowRefNote';
import { parseOverBody, serializeOverBody, type OverBodySpec } from './overBodySpec';
import { WindowSpecEditor } from './WindowSpecEditor';

const TOOLTIP_DISABLED = vqeCopy.disabled.windowStructuredUnavailable;

export interface WindowOverEditorProps {
  readonly item: WindowItem;
  readonly itemIndex: number;
  readonly testIdPrefix: string;
  readonly options: ParseOptions;
  readonly editContext?: VqeEditContext;
}

function seedOverText(item: WindowItem): string {
  return item.overBody !== null ? `OVER (${item.overBody})` : `OVER ${item.namedWindow ?? ''}`;
}

function recordFailure(error: unknown): void {
  useVqeStore.getState().recordCommitFeedback({
    kind: 'failure',
    at: Date.now(),
    message: error instanceof Error ? error.message : String(error),
  });
}

export function WindowOverEditor({ item, itemIndex, testIdPrefix, options, editContext }: WindowOverEditorProps) {
  const [textMode, setTextMode] = useState(false);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (editing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.select();
    }
  }, [editing]);

  const hasOverBody = item.overBody !== null;
  const hasNamedWindow = item.namedWindow !== null;
  const editReady = Boolean(editContext) && Boolean(editContext?.service.getSchemaSnapshot());

  const spec = useMemo(
    () => (hasOverBody ? parseOverBody(item.overBody ?? '', options) : null),
    [hasOverBody, item.overBody, options],
  );
  const structuredMode = editReady && hasOverBody && !!spec?.parseable && !textMode;

  const commitSpec = (next: OverBodySpec): void => {
    if (!editContext || !editReady) return;
    const overText = `OVER (${serializeOverBody(next)})`;
    void modifySelectWindow(itemIndex, overText, toCommitCtx(editContext)).catch(recordFailure);
  };

  const startEditing = (): void => {
    setValue(seedOverText(item));
    setEditing(true);
  };
  const cancel = (): void => {
    setEditing(false);
    setTextMode(false); // Escape from the "Edit as text" escape hatch → back to structured.
  };

  const commit = (): void => {
    const trimmed = value.trim();
    const seed = seedOverText(item);
    if (!editContext || !editReady) { setEditing(false); return; }
    if (trimmed.length === 0 || trimmed === seed.trim()) { setEditing(false); return; }
    // `modifySelectWindow` throws unless the payload starts with OVER — pre-
    // validate so the editor stays OPEN for the user to fix the text.
    if (!/^over\b/i.test(trimmed)) {
      useVqeStore.getState().recordCommitFeedback({
        kind: 'failure',
        at: Date.now(),
        message: 'the OVER clause must start with OVER — nothing was changed',
      });
      return;
    }
    setEditing(false);
    setTextMode(false);
    void modifySelectWindow(itemIndex, value, toCommitCtx(editContext)).catch(recordFailure);
  };

  // Structured editor — the primary edit surface when ready + parseable.
  if (structuredMode && editContext && spec) {
    return (
      <>
        <WindowSpecEditor
          spec={spec}
          onChange={commitSpec}
          editContext={editContext}
          testIdPrefix={testIdPrefix}
          engine={options.engine}
        />
        <span className="flex items-center justify-end">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => { setTextMode(true); startEditing(); }}
            data-testid={`${testIdPrefix}-edit-as-text`}
            className="h-6 px-1.5 text-[11px] text-muted-foreground"
          >
            {vqeCopy.window.editAsText}
          </Button>
        </span>
      </>
    );
  }

  const showTextFallbackNote = editReady && hasOverBody && !!spec && !spec.parseable;

  return (
    <>
      {editing ? (
        <>
          {showTextFallbackNote ? (
            <p className="text-[11px] italic text-muted-foreground" data-testid={`${testIdPrefix}-text-fallback-note`}>
              {vqeCopy.window.textFallbackNote}
            </p>
          ) : null}
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); commit(); }
              else if (e.key === 'Escape') { e.preventDefault(); cancel(); }
            }}
            aria-label="Edit OVER clause"
            data-testid={`${testIdPrefix}-over-editor`}
            className="m-0 max-h-48 w-full resize-y overflow-auto rounded border border-input bg-background p-2 font-mono text-[11px] leading-relaxed"
          />
        </>
      ) : (
        <>
          {hasOverBody ? (
            <pre
              className="m-0 max-h-48 overflow-auto rounded bg-background p-2 font-mono text-[11px] leading-relaxed"
              data-testid={`${testIdPrefix}-over-body`}
            >
              {item.overBody}
            </pre>
          ) : null}
          {hasNamedWindow ? (
            <NamedWindowRefNote
              name={item.namedWindow ?? ''}
              options={options}
              testIdPrefix={testIdPrefix}
              editContext={editContext}
            />
          ) : null}
          {showTextFallbackNote ? (
            <p className="text-[11px] italic text-muted-foreground" data-testid={`${testIdPrefix}-text-fallback-note`}>
              {vqeCopy.window.textFallbackNote}
            </p>
          ) : null}
        </>
      )}
      <span className="flex items-center justify-between gap-2">
        <span className="text-[10px] italic text-muted-foreground" data-testid={`${testIdPrefix}-opaque-hint`}>
          {editReady ? '' : vqeCopy.hint.windowCustomFrame}
        </span>
        {editReady ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={startEditing}
            data-testid={`${testIdPrefix}-edit-over`}
            className="h-6 gap-1 px-1.5 text-[11px] text-muted-foreground"
          >
            <Pencil className="h-3 w-3" />
            {vqeCopy.actions.editOver}
          </Button>
        ) : (
          <VqeTooltip content={TOOLTIP_DISABLED}>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled
              aria-disabled
              data-testid={`${testIdPrefix}-edit-fields`}
              className="h-6 gap-1 px-1.5 text-[11px] text-muted-foreground"
            >
              <Pencil className="h-3 w-3" />
              edit fields
            </Button>
          </VqeTooltip>
        )}
      </span>
    </>
  );
}
